import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LoaderCircle, CheckCircle2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PillButton } from "@/components/PillButton";
import { submitInquiry, getErrorMessage } from "@/lib/api";
import { useServices, useProducts } from "@/hooks/useSiteData";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", email: "", phone: "", company: "", subject: "", service_interest: "", message: "" };

export const ContactForm = ({ defaultInterest = "", compact = false, bare = false }) => {
  const location = useLocation();
  const { data: services = [] } = useServices();
  const { data: products = [] } = useProducts();
  const [form, setForm] = useState({ ...EMPTY, service_interest: defaultInterest });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setErrors((er) => ({ ...er, [k]: undefined })); };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your full name.";
    if (!EMAIL_RX.test(form.email)) er.email = "Please enter a valid work email.";
    if (form.message.trim().length < 5) er.message = "Tell us a little more about your requirement (min 5 characters).";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await submitInquiry({ ...form, source_page: location.pathname });
      setDone(res.message);
      toast.success("Inquiry sent", { description: res.message });
    } catch (err) {
      toast.error("Could not send your inquiry", { description: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const wrap = bare ? "" : "card-solid p-6 sm:p-8";
  const inputCls = "h-12 rounded-xl border-border bg-white px-4";

  if (done) {
    return (
      <div className={`${bare ? "" : "card-solid"} flex flex-col items-center gap-4 p-10 text-center`} role="status" data-testid="contact-form-success">
        <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10"><CheckCircle2 className="h-8 w-8 text-primary" /></span>
        <h3 className="font-heading text-2xl font-medium">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="max-w-md text-muted-foreground">{done}</p>
        <PillButton variant="outline" size="sm" onClick={() => { setDone(null); setForm({ ...EMPTY }); }} testId="contact-form-send-another-button">Send another message</PillButton>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className={`${wrap} space-y-5`} data-testid="contact-form">
      <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
        <div className="space-y-1.5">
          <Label htmlFor="cf-name" className="text-[13px]">Full name <span className="text-primary">*</span></Label>
          <Input id="cf-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} aria-invalid={!!errors.name} data-testid="contact-form-name-input" />
          {errors.name && <p className="text-xs text-destructive" data-testid="contact-form-name-error">{errors.name}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cf-email" className="text-[13px]">Work email <span className="text-primary">*</span></Label>
          <Input id="cf-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} aria-invalid={!!errors.email} data-testid="contact-form-email-input" />
          {errors.email && <p className="text-xs text-destructive" data-testid="contact-form-email-error">{errors.email}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cf-phone" className="text-[13px]">Phone</Label>
          <Input id="cf-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className={inputCls} data-testid="contact-form-phone-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cf-company" className="text-[13px]">Company</Label>
          <Input id="cf-company" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} data-testid="contact-form-company-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cf-interest" className="text-[13px]">I&apos;m interested in</Label>
          <Select value={form.service_interest} onValueChange={(v) => setForm((f) => ({ ...f, service_interest: v }))}>
            <SelectTrigger id="cf-interest" className={inputCls} data-testid="contact-form-interest-select"><SelectValue placeholder="Select a service or product" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="General enquiry">General enquiry</SelectItem>
              {services.map((s) => <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>)}
              {products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name} (product)</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cf-subject" className="text-[13px]">Subject</Label>
          <Input id="cf-subject" value={form.subject} onChange={set("subject")} placeholder="e.g. Cloud migration for retail chain" className={inputCls} data-testid="contact-form-subject-input" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cf-message" className="text-[13px]">How can we help? <span className="text-primary">*</span></Label>
        <Textarea id="cf-message" rows={compact ? 4 : 5} value={form.message} onChange={set("message")} placeholder="Share your goals, timelines and any context that helps us prepare." className="rounded-xl border-border bg-white px-4 py-3" aria-invalid={!!errors.message} data-testid="contact-form-message-input" />
        {errors.message && <p className="text-xs text-destructive" data-testid="contact-form-message-error">{errors.message}</p>}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">We respect your privacy. Your details are only used to respond to this enquiry.</p>
        <PillButton type="submit" variant="primary" disabled={loading} icon={loading ? LoaderCircle : Send} className={loading ? "[&_svg]:animate-spin" : ""} testId="contact-form-submit-button">{loading ? "Sending..." : "Send message"}</PillButton>
      </div>
    </form>
  );
};
